export const isAbortError = (error: unknown): boolean => {
  return error instanceof Error && error.name === 'AbortError';
};

interface AbortHandler {
  abort: (reason?: Error) => void;
  signal: AbortSignal;
}

export const activeRequests: Record<string, AbortHandler | null> = {};

/**
 * Creates an abortable request with a timeout.
 * @param endpoint - The endpoint of the request.
 * @param requestFn - The function to make the request.
 * @param timeoutMs - The timeout in milliseconds.
 * @returns A promise that resolves to the response of the request.
 */
export const makeAbortableRequest = async <T>(
  endpoint: string,
  requestFn: (signal: AbortSignal) => Promise<T>,
  timeoutMs = 25000
): Promise<T> => {
  if (activeRequests[endpoint]) {
    activeRequests[endpoint]?.abort(
      new Error('Request Aborted, Reason: New request started')
    );
    activeRequests[endpoint] = null;
  }

  const controller = new AbortController();
  const { signal } = controller;

  const timeoutId = setTimeout(() => {
    controller.abort(
      new Error(
        `Request Aborted, Reason: Request timed out after ${timeoutMs}ms`
      )
    );
  }, timeoutMs);

  const abort = (
    reason = new Error('Request Aborted, Reason: Operation canceled by user')
  ) => {
    clearTimeout(timeoutId);
    controller.abort(reason);
    if (activeRequests[endpoint]?.abort === abort) {
      activeRequests[endpoint] = null;
    }
  };

  activeRequests[endpoint] = { abort, signal };

  return requestFn(signal).finally(() => {
    clearTimeout(timeoutId);
    if (activeRequests[endpoint]?.abort === abort) {
      activeRequests[endpoint] = null;
    }
  });
};

/**
 * Stops an active request by its endpoint id.
 * @param endpoint - The id of the request to stop.
 * @param reason - Optional reason for aborting the request.
 */
export function stopRequestById(
  endpoint: string,
  reason = 'Request Aborted, Reason: Stopped by user'
): void {
  const handler = activeRequests[endpoint];
  if (!handler) return;
  handler.abort(new Error(reason));
}
